import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { reduceAmount } from '@shared/utils/reduce-price-symbols';
import { isNil } from 'lodash';

import MasterFilterService2 from './master-filter.service2';
@Component({
    selector: 'app-range-slider',
    templateUrl: './range-slider.component.html',
    styleUrls: ['./range-slider.component.less', './master-filter.component.less'],
    standalone: false,
})
class RangeSliderComponent implements OnChanges {
    @Input() title: string = '';

    @Input() defaultAreaTitle = '';

    @Input() stepInput: number = 5000;

    @Input() valueMin: number = null;

    @Input() valueMax: number = null;

    @Input() suffix: string = '';

    @Input() type: string = '';

    @Input() valueFrom: number = null;

    @Input() valueTo: number = null;

    @Output() onSubmitForm: EventEmitter<{ valueFrom: number; valueTo: number }> =
        new EventEmitter<{ valueFrom: number; valueTo: number }>();

    label: string = '';

    labelWithoutTitle: string = '';

    rangeValuesTotal = [this.valueFrom, this.valueTo];

    labelDefSquare = /\s*\(.*?\)\s*/g;

    rangeForm: FormGroup;

    constructor(private fb: FormBuilder, private masterFilterService: MasterFilterService2) {
        this.rangeForm = this.fb.group({
            valueFrom: null,
            valueTo: null,
        });


        // Subscribe to form value changes
        this.rangeForm.valueChanges.subscribe(values => {
            this.valueFrom = values.valueFrom;
            this.valueTo = values.valueTo;

            // Update slider values when form values change
            this.rangeValuesTotal = [this.valueFrom, this.valueTo];

            this.onSubmitForm.emit({
                valueFrom: this.rangeForm.get('valueFrom').value,
                valueTo: this.rangeForm.get('valueTo').value,
            });
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.valueFrom || changes.valueTo) {
            this.rangeForm.patchValue({
                valueFrom: this.valueFrom,
                valueTo: this.valueTo,
            });
            this.updateLabel();
        }
    }

    // When the slider changes, update the form controls
    onSliderChange(event) {
        const [min, max] = event.values;
        this.rangeValuesTotal = [min, max];

        this.rangeForm.patchValue({
            valueFrom: min,
            valueTo: max,
        }, { emitEvent: false });

        this.updateLabel();
    }

    updateValueFrom(value: number) {
        value = this.validateValue(value);
        this.rangeForm.get('valueFrom')
            .setValue(value);
        this.valueFrom = value;
        this.rangeValuesTotal = [this.valueFrom || this.valueMin, this.valueTo || this.valueMax];
        this.updateLabel();
    }

    updateValueTo(value: number) {
        value = this.validateValue(value);
        this.rangeForm.get('valueTo')
            .setValue(value);
        this.valueTo = value;
        this.rangeValuesTotal = [this.valueFrom || this.valueMin, this.valueTo || this.valueMax];
        this.updateLabel();
    }

    updateLabel() {
        const valueFromRaw = this.rangeForm.get('valueFrom')?.value;
        const valueToRaw = this.rangeForm.get('valueTo')?.value;

        const hasFrom = !isNil(valueFromRaw);
        const hasTo = !isNil(valueToRaw);

        if (hasFrom && hasTo) {
            const valueFrom = reduceAmount(valueFromRaw);
            const valueTo = reduceAmount(valueToRaw);
            this.label = `${this.title} от ${valueFrom} ${this.suffix} - до ${valueTo} ${this.suffix}`;
        } else if (hasFrom) {
            const valueFrom = reduceAmount(valueFromRaw);
            this.label = `${this.title} от ${valueFrom} ${this.suffix}`;
        } else if (hasTo) {
            const valueTo = reduceAmount(valueToRaw);
            this.label = `${this.title} до ${valueTo} ${this.suffix}`;
        } else {
            this.label = this.defaultAreaTitle || '';
        }

        this.labelWithoutTitle = this.label.replace(this.title, '')
            .trim();

        if (this.type === 'area') {
            this.title = this.title.replace(this.labelDefSquare, '');
            this.masterFilterService.updateAreaLabel(this.label);
        }

        if (this.type === 'price') {
            this.masterFilterService.updatePriceLabel(this.label);
        }

        if (this.type === 'priceSquare') {
            this.masterFilterService.updatePriceSquareLabel(this.label);
        }
    }

    validateValue(value: number): number {
        if (value < this.valueMin) {
            value = this.valueMin;
        }
        if (value > this.valueMax) {
            value = this.valueMax;
        }

        return value;
    }

    submitForm(event: Event) {
        event.stopPropagation();

        this.onSubmitForm.emit({
            valueFrom: this.rangeForm.get('valueFrom').value,
            valueTo: this.rangeForm.get('valueTo').value,
        });
    }

    clearValueFrom() {
        this.valueFrom = null;
        this.rangeValuesTotal[0] = null;

        this.rangeForm.patchValue({ valueFrom: null });
        this.updateLabel();
    }


    clearValueTo() {
        this.valueTo = null;
        this.rangeValuesTotal[1] = null;

        this.rangeForm.patchValue({ valueTo: null });
        this.updateLabel();
    }

    clearValues() {
        this.clearValueFrom();
        this.clearValueTo();
        this.updateLabel();
    }

}

export default RangeSliderComponent;
